import React, { useEffect, useState } from "react";
import Rating from "@mui/material/Rating";
import { FaShoppingCart, FaHeart, FaRegHeart } from "react-icons/fa";
import { FaRegCopy } from "react-icons/fa6";
import { BsInfoCircle } from "react-icons/bs";
import { toast } from "sonner";
import {
  fetchWalmartProductDetails,
  fetchWalmartSellers,
  fetchWalmartReviews,
} from "../../services/walmart";
import { addFavorite, removeFavorite } from "../../services/product";

const WalmartProductPage = ({ productId }) => {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [offersTab, setOffersTab] = useState("Current");
  const [allOffersLoading, setAllOffersLoading] = useState(false);
  const [allOffersError, setAllOffersError] = useState(null);
  const [allOffers, setAllOffers] = useState(null);
  const [reviewsData, setReviewsData] = useState(null);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsError, setReviewsError] = useState(null);
  const [expandedSection, setExpandedSection] = useState({
    offers: true,
    badges: true,
    description: true,
    highlights: true,
    specifications: true,
    shipping: true,
    reviews: true,
    images: true,
    variants: true,
    categories: true,
  });
  const [variantsExpanded, setVariantsExpanded] = useState({});
  const VARIANTS_VISIBLE_INITIAL = 4;

  const handleAddToFavorites = async () => {
    if (!product?.product_id) return;
    setIsFavorite(true);
    try {
      await addFavorite(product.product_id);
      toast.success("Added to favorites");
    } catch (err) {
      console.error("Error adding favorite:", err);
    }
  };

  const handleRemoveFromFavorites = async () => {
    if (!product?.product_id) return;
    setIsFavorite(false);
    try {
      await removeFavorite(product.product_id);
      toast.success("Removed from favorites");
    } catch (err) {
      console.error("Error removing favorite:", err);
    }
  };

  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      /* Global helper to hide scrollbars when needed */
      .hide-scrollbar::-webkit-scrollbar {
        display: none;
      }
      .hide-scrollbar {
        -ms-overflow-style: none;
        scrollbar-width: none;
      }

      /* Themed horizontal scrollbar for Offers and other wide tables */
      .offers-scroll {
        scrollbar-color: #4ade80 #06260a; /* thumb, track */
        scrollbar-width: thin;
      }

      .offers-scroll::-webkit-scrollbar {
        height: 6px;
      }

      .offers-scroll::-webkit-scrollbar-track {
        background: #06260a;
      }

      .offers-scroll::-webkit-scrollbar-thumb {
        background-color: #4ade80;
        border-radius: 9999px;
      }

      .offers-scroll::-webkit-scrollbar-thumb:hover {
        background-color: #22c55e;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  useEffect(() => {
    if (!productId) return;

    const loadProduct = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetchWalmartProductDetails(productId);
        const productData = response?.data || null;
        setProduct(productData);

        // Fetch reviews using separate Walmart reviews API
        if (productData?.us_item_id || productData?.product_id) {
          try {
            setReviewsLoading(true);
            setReviewsError(null);
            const reviewsResponse = await fetchWalmartReviews(
              productData.us_item_id || productData.product_id
            );
            setReviewsData(reviewsResponse?.data || null);
          } catch (reviewsErr) {
            console.error("Error loading Walmart reviews:", reviewsErr);
            setReviewsError("Failed to load reviews for this product.");
          } finally {
            setReviewsLoading(false);
          }
        }
      } catch (err) {
        console.error("Error loading Walmart product:", err);
        setError("Failed to load Walmart product details.");
      } finally {
        setLoading(false);
      }
    };

    loadProduct();
  }, [productId]);

  const toggleSection = (section) => {
    setExpandedSection((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-primary-bg px-4 py-6">
        <div className="text-center text-primary mt-6">
          <div className="loading-spinner"></div>
          <p>Loading Walmart product...</p>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-primary-bg px-4 py-6">
        <div className="text-center text-primary">
          <p className="mb-2">{error || "Product not found."}</p>
          <p className="text-xs opacity-70">
            Try another Walmart product ID or search again.
          </p>
        </div>
      </div>
    );
  }

  const mainImage = product.main_image || (product.images && product.images[0]);
  const price = product.price || {};
  const images = product.images || [];
  const specs = product.specifications || {};
  const highlights = product.highlights || [];
  const baseOffers = product.offers || [];
  const liveOffers = baseOffers;
  const sellersOffers =
    allOffers && Array.isArray(allOffers.sellers) ? allOffers.sellers : [];
  const primaryOffer =
    Array.isArray(baseOffers) && baseOffers.length > 0 ? baseOffers[0] : null;
  const sellerCount =
    product.seller_count ??
    product.seller_options?.number_of_additional_sellers ??
    null;
  const deliveryArrival = product.delivery_arrival || null;
  const reviewsPayload = reviewsData || {};
  const topPositive = reviewsPayload.top_positive || null;
  const topNegative = reviewsPayload.top_negative || null;
  const ratingBreakdown = Array.isArray(reviewsPayload.ratings)
    ? reviewsPayload.ratings
    : [];
  const ratingTotalCount =
    typeof reviewsPayload.total_count === "number"
      ? reviewsPayload.total_count
      : Array.isArray(ratingBreakdown)
      ? ratingBreakdown.reduce((sum, r) => sum + (r.count || 0), 0)
      : 0;

  // Offer stats: derive counts purely from visible offers on frontend
  const currentOffersCount = Array.isArray(liveOffers) ? liveOffers.length : 0;
  const allOffersCount =
    Array.isArray(sellersOffers) && sellersOffers.length > 0
      ? sellersOffers.length
      : currentOffersCount;
  const visibleOffersCount =
    offersTab === "All Offers" ? allOffersCount : currentOffersCount;
  const fbaCount = 0;
  const fbmCount = 0;
  const amazonCount = 0;

  const nextImage = () => {
    setSelectedImage((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setSelectedImage((prev) => (prev - 1 + images.length) % images.length);
  };

  const handleOpenGoogleSearch = () => {
    const query = (product?.title || "").trim();
    if (!query) return;
    const googleUrl = `https://www.google.com/search?q=${encodeURIComponent(
      query
    )}`;
    window.open(googleUrl, "_blank");
  };

  return (
    <div className="w-full text-primary px-3 py-4 pb-20">
      <div className="border border-primary/30 bg-primary-bg rounded-2xl overflow-hidden">
        {/* Quick Info Header */}
        <div className="p-4">
          <h2 className="text-primary font-bold text-left text-base">
            Quick Info
          </h2>

          {/* Product Image */}
          <div
            className={`w-full my-4 rounded-lg overflow-hidden ${
              mainImage ? "p-6 bg-white" : "py-0"
            }`}
          >
            {!mainImage ? (
              <div
                className="flex items-center justify-center h-36 bg-gray-300 rounded w-full"
                style={{
                  border: "0.5px solid #376C46",
                  background: "rgba(118, 249, 155, 0.15)",
                  borderRadius: "20px",
                }}
              >
                <svg
                  className="w-10 h-10 text-gray-200 dark:text-[#76F99B]"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="#1c211d"
                  viewBox="0 0 20 18"
                >
                  <path d="M18 0H2a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2Zm-5.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Zm4.376 10.481A1 1 0 0 1 16 15H4a1 1 0 0 1-.895-1.447l3.5-7A1 1 0 0 1 7.468 6a.965.965 0 0 1 .9.5l2.775 4.757 1.546-1.887a1 1 0 0 1 1.618.1l2.541 4a1 1 0 0 1 .028 1.011Z" />
                </svg>
              </div>
            ) : (
              <img
                src={mainImage}
                alt={product.title || "Product"}
                className="w-full h-36 object-contain"
                style={{
                  borderRadius: "20px",
                }}
              />
            )}
          </div>

          {/* Product Title and Info */}
          <div className="text-primary text-left w-full">
            {product.brand && (
              <div className="text-white font-bold text-xs">
                {product.brand}
              </div>
            )}
            <div className="group relative text-xl font-bold cursor-default">
              {product.title
                ? product.title.length > 60
                  ? product.title.substring(0, 60) + "..."
                  : product.title
                : "--"}
            </div>
            {product.category && (
              <div className="text-white" style={{ fontSize: "11px" }}>
                {product.category}
              </div>
            )}

            {/* Rating */}
            {(() => {
              const hasReviewsRating =
                typeof reviewsPayload.overall_rating === "number";
              const displayRating = hasReviewsRating
                ? reviewsPayload.overall_rating
                : product.rating?.value ?? null;
              const displayCount =
                typeof reviewsPayload.total_count === "number"
                  ? reviewsPayload.total_count
                  : product.rating?.count ?? 0;

              if (displayRating == null) return null;

              return (
                <div className="flex items-center justify-between mt-1">
                  <div className="flex items-end">
                    <Rating
                      name="read-only"
                      value={Number(displayRating)}
                      precision={0.1}
                      readOnly
                      style={{
                        fontSize: "18px",
                        color: "#75fa9b",
                      }}
                    />
                    <span className="text-primary text-xs font-semibold ml-1">
                      {displayCount ? displayCount : 0}
                    </span>
                  </div>

                  {/* Favorite button */}
                  <button
                    onClick={
                      isFavorite ? handleRemoveFromFavorites : handleAddToFavorites
                    }
                    className="bg-secondary hover:bg-primary hover:text-secondary transition duration-300 text-primary p-1.5 px-3 rounded-md flex items-center justify-center ml-3"
                  >
                    {isFavorite ? (
                      <FaHeart className="w-4 h-4" />
                    ) : (
                      <FaRegHeart className="w-4 h-4" />
                    )}
                  </button>
                </div>
              );
            })()}
          </div>

          {/* Product IDs Section */}
          <div className="my-4 flex justify-between gap-4">
            {product.product_id && (
              <div className="text-white text-xs">
                <p className="text-white text-left text-xs mb-1">Product ID</p>
                <div className="flex items-center gap-x-1">
                  <p className="text-xs text-gray-500">{product.product_id}</p>
                  <span
                    className="cursor-pointer text-primary bg-secondary rounded-md p-1 px-1.5"
                    onClick={() => {
                      navigator.clipboard.writeText(product.product_id);
                      toast.success("Product ID copied to clipboard");
                    }}
                  >
                    <FaRegCopy className="w-3 h-3" />
                  </span>
                </div>
              </div>
            )}
            {product.us_item_id && (
              <div className="text-white text-xs">
                <p className="text-white text-left text-xs mb-1">Item ID</p>
                <div className="flex items-center gap-x-1">
                  <p className="text-xs text-gray-500">{product.us_item_id}</p>
                  <span
                    className="cursor-pointer text-primary bg-secondary rounded-md p-1 px-1.5"
                    onClick={() => {
                      navigator.clipboard.writeText(product.us_item_id);
                      toast.success("Item ID copied to clipboard");
                    }}
                  >
                    <FaRegCopy className="w-3 h-3" />
                  </span>
                </div>
              </div>
            )}
            {product.upc && (
              <div className="text-white text-xs">
                <p className="text-white text-left text-xs mb-1">UPC</p>
                <div className="flex items-center gap-x-1">
                  <p className="text-xs text-gray-500">{product.upc}</p>
                  <span
                    className="cursor-pointer text-primary bg-secondary rounded-md p-1 px-1.5"
                    onClick={() => {
                      navigator.clipboard.writeText(product.upc);
                      toast.success("UPC copied to clipboard");
                    }}
                  >
                    <FaRegCopy className="w-3 h-3" />
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Icon Buttons Row - Info opens Google search; cart opens Walmart link */}
          <div className="flex justify-between gap-1.5 w-full mt-2">
            {(product.description || product.link) && (
              <div
                className="flex items-center justify-center group relative w-full bg-secondary rounded-md px-2 py-2 text-primary hover:bg-primary hover:text-secondary transition duration-300 cursor-pointer"
                onClick={handleOpenGoogleSearch}
                title="Search product name on Google"
              >
                <BsInfoCircle className="w-3.5 h-3.5" />
              </div>
            )}
            {product.link && (
              <div
                className="flex items-center justify-center group relative w-full cursor-pointer bg-secondary rounded-md px-2 py-2 text-primary hover:bg-primary hover:text-secondary transition duration-300"
                onClick={() => window.open(product.link, "_blank")}
                title="Open product on Walmart in browser"
              >
                <FaShoppingCart className="w-3.5 h-3.5" />
              </div>
            )}
          </div>
        </div>

        {/* Price and Availability Section */}
        <div className="px-4 pb-4 space-y-3">
          {/* Price & Availability */}
          <div className="bg-secondary-bg rounded-lg p-4">
            <div className="flex items-baseline gap-3 flex-wrap mb-3">
              {price.current != null && (
                <div className="flex items-baseline gap-1">
                  <span className="text-sm text-primary/70">
                    {price.currency || "USD"}
                  </span>
                  <span className="text-3xl font-bold text-primary">
                    {price.current}
                  </span>
                </div>
              )}
              {price.was != null && price.was !== price.current && (
                <div className="flex items-center gap-2">
                  <span className="text-sm line-through text-primary/50">
                    {price.currency || "USD"} {price.was}
                  </span>
                  {price.savings != null && (
                    <span className="text-xs font-semibold text-green-400 bg-green-400/10 px-2 py-1 rounded">
                      Save {price.currency || "USD"} {price.savings}
                    </span>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-2 text-sm border-t border-primary/20 pt-3">
              {product.availability && (
                <div className="flex items-center justify-between">
                  <span className="text-primary/70 text-xs">
                    Availability:
                  </span>
                  <span className="font-semibold text-green-400 text-xs">
                    {product.availability}
                  </span>
                </div>
              )}

              {product.seller?.name && (
                <div className="flex items-center justify-between">
                  <span className="text-primary/70 text-xs">Sold by:</span>
                  <span className="font-semibold text-white text-xs">
                    {product.seller.name}
                  </span>
                </div>
              )}

              {product.min_quantity && product.max_quantity && (
                <div className="flex items-center justify-between">
                  <span className="text-primary/70 text-xs">
                    Order Limits:
                  </span>
                  <span className="text-white text-xs">
                    Min: {product.min_quantity} | Max: {product.max_quantity}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Description Section */}
          {product.description && (
            <div className="border border-primary/20 rounded-xl overflow-hidden">
              <button
                onClick={() => toggleSection("description")}
                className="w-full bg-secondary-bg hover:bg-primary/10 transition p-4 flex items-center justify-between"
              >
                <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                  Product Description
                </h3>
                <span className="text-primary/70">
                  {expandedSection.description ? "−" : "+"}
                </span>
              </button>
              {expandedSection.description && (
                <div className="p-4 border-t border-primary/20 bg-primary-bg">
                  <div
                    className="text-sm text-primary/90 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: product.description }}
                  />
                </div>
              )}
            </div>
          )}

          {/* Shipping Info */}
          {product.shipping && (
            <div className="border border-primary/20 rounded-xl overflow-hidden">
              <button
                onClick={() => toggleSection("shipping")}
                className="w-full bg-secondary-bg hover:bg-primary/10 transition p-4 flex items-center justify-between"
              >
                <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                  Shipping Information
                </h3>
                <span className="text-primary/70">
                  {expandedSection.shipping ? "−" : "+"}
                </span>
              </button>
              {expandedSection.shipping && (
                <div className="p-4 border-t border-primary/20 bg-primary-bg">
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-primary text-xs">
                        Free Shipping:
                      </span>
                      <span
                        className={`text-xs font-semibold ${
                          product.shipping.free_shipping
                            ? "text-green-400"
                            : "text-red-400"
                        }`}
                      >
                        {product.shipping.free_shipping ? "Yes" : "No"}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-primary text-xs">
                        Pickup Available:
                      </span>
                      <span
                        className={`text-xs font-semibold ${
                          product.shipping.pickup_available
                            ? "text-green-400"
                            : "text-red-400"
                        }`}
                      >
                        {product.shipping.pickup_available ? "Yes" : "No"}
                      </span>
                    </div>
                    {deliveryArrival && (
                      <div className="flex items-center justify-between">
                        <span className="text-primary text-xs">
                          Earliest Delivery:
                        </span>
                        <span className="text-white text-xs">
                          {new Date(deliveryArrival).toLocaleDateString(
                            "en-US",
                            {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            }
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Offers Section */}
          <div className="border border-primary/20 rounded-xl overflow-hidden">
            <button
              onClick={() => toggleSection("offers")}
              className="w-full bg-secondary-bg hover:bg-primary/10 transition p-4 flex items-center justify-between"
            >
              <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                Offers
              </h3>
              <span className="text-primary/70">
                {expandedSection.offers ? "−" : "+"}
              </span>
            </button>
            {expandedSection.offers && (
              <div className="p-4 border-t border-primary/20 bg-primary-bg">
                {/* Tabs */}
                <div className="flex items-center bg-secondary-bg rounded-md mb-3">
                  {["Current", "All Offers"].map((option) => (
                    <button
                      key={option}
                      className={`cursor-pointer w-full px-3 py-2 text-xs font-semibold rounded-md ${
                        offersTab === option
                          ? "bg-primary text-primary-bg"
                          : "text-primary"
                      }`}
                      onClick={async () => {
                        setOffersTab(option);
                        if (
                          option === "All Offers" &&
                          !allOffers &&
                          product.us_item_id
                        ) {
                          try {
                            setAllOffersLoading(true);
                            setAllOffersError(null);
                            const response = await fetchWalmartSellers(
                              product.us_item_id
                            );
                            setAllOffers(response?.data || null);
                          } catch (err) {
                            console.error("Error fetching all offers:", err);
                            setAllOffersError(
                              "Failed to load all offers for this product."
                            );
                          } finally {
                            setAllOffersLoading(false);
                          }
                        }
                      }}
                    >
                      {option}
                    </button>
                  ))}
                </div>

                {/* Table with horizontal scroll on small screens, themed scrollbar */}
                <div className="relative overflow-x-auto offers-scroll">
                  <table
                    className="w-full min-w-[520px] mt-2"
                    style={{
                      fontSize: "13px",
                      borderCollapse: "collapse",
                    }}
                  >
                    <thead>
                      <tr className="text-xs font-semibold">
                        <td>
                          <div className="flex items-center justify-between px-2">
                            <div className="p-1 flex-2">#</div>
                            <div className="p-1 flex-1">Seller</div>
                            <div className="p-1 flex-1">Price</div>
                            <div className="p-1 flex-1">Type</div>
                            {offersTab === "All Offers" && (
                              <div className="p-1 flex-1">Availability</div>
                            )}
                          </div>
                        </td>
                      </tr>
                    </thead>
                    <tbody>
                      {offersTab === "All Offers" ? (
                        allOffersLoading ? (
                          <tr>
                            <td className="p-0">
                              <div
                                className="text-center text-primary/70 bg-secondary-bg p-4 rounded-lg mt-2"
                                style={{
                                  marginBottom: "4px",
                                  padding: "10px",
                                }}
                              >
                                Loading all offers...
                              </div>
                            </td>
                          </tr>
                        ) : allOffersError ? (
                          <tr>
                            <td className="p-0">
                              <div
                                className="text-center text-red-400 bg-secondary-bg p-4 rounded-lg mt-2"
                                style={{
                                  marginBottom: "4px",
                                  padding: "10px",
                                }}
                              >
                                {allOffersError}
                              </div>
                            </td>
                          </tr>
                        ) : sellersOffers.length === 0 ? (
                          <tr>
                            <td className="p-0">
                              <div
                                className="text-center text-primary/70 bg-secondary-bg p-4 rounded-lg mt-2"
                                style={{
                                  marginBottom: "4px",
                                  padding: "10px",
                                }}
                              >
                                No offers available yet for this Walmart
                                product.
                              </div>
                            </td>
                          </tr>
                        ) : (
                          sellersOffers.map((seller, index) => (
                            <tr key={seller.offer_id || index}>
                              <td className="p-0">
                                <div
                                  className="text-left text-white bg-secondary-bg p-4 rounded-lg"
                                  style={{
                                    marginBottom: "4px",
                                    marginTop: index === 0 ? "0" : "4px",
                                    padding: "10px",
                                  }}
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="p-1 flex-2 text-center">
                                      {seller.position ?? index + 1}
                                    </div>
                                    <div className="p-1 flex-1 text-center">
                                      {seller.seller_display_name ||
                                        seller.seller_name ||
                                        "Seller"}
                                    </div>
                                    <div className="p-1 flex-1 text-center">
                                      {seller.price || "-"}
                                    </div>
                                    <div className="p-1 flex-1 text-primary text-center">
                                      {seller.offer_type || "—"}
                                    </div>
                                    <div className="p-1 flex-1 text-primary text-center">
                                      {seller.availability_status || "—"}
                                    </div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          ))
                        )
                      ) : liveOffers.length === 0 ? (
                        <tr>
                          <td className="p-0">
                            <div
                              className="text-center text-primary/70 bg-secondary-bg p-4 rounded-lg mt-2"
                              style={{
                                marginBottom: "4px",
                                padding: "10px",
                              }}
                            >
                              No offers available yet for this Walmart product.
                            </div>
                          </td>
                        </tr>
                      ) : (
                        liveOffers.map((offer, index) => (
                          <tr key={offer.seller_id || index}>
                            <td className="p-0">
                              <div
                                className="text-left text-white bg-secondary-bg p-4 rounded-lg"
                                style={{
                                  marginBottom: "4px",
                                  marginTop: index === 0 ? "0" : "4px",
                                  padding: "10px",
                                }}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="p-1 flex-2 text-center">
                                    {index + 1}
                                  </div>
                                  <div className="p-1 flex-1 text-center">
                                    {offer.seller_display_name ||
                                      offer.seller_name ||
                                      product.seller?.name ||
                                      "Seller"}
                                  </div>
                                  <div className="p-1 flex-1 text-center">
                                    {price.currency || "USD"}{" "}
                                    {offer.price ?? price.current ?? "-"}
                                  </div>
                                  <div className="p-1 flex-1 text-primary text-center">
                                    {product.offer_type || "—"}
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>

                  {/* Summary chips */}
                  <div className="flex mb-2 text-white text-xs font-semibold mt-3">
                    <div className="cursor-pointer bg-secondary px-2 py-1.5 rounded-lg mr-1">
                      Offers: {visibleOffersCount}
                    </div>

                    {fbaCount > 0 && (
                      <div className="cursor-pointer bg-[#592810] border border-[#d5a109] px-2 py-1 rounded-lg mr-1">
                        FBA: {fbaCount}
                      </div>
                    )}

                    {fbmCount > 0 && (
                      <div className="cursor-pointer bg-blue-900/40 border border-blue-500 px-2 py-1 rounded-lg mr-1">
                        FBM: {fbmCount}
                      </div>
                    )}

                    {amazonCount > 0 && (
                      <div className="cursor-pointer bg-[#644513] border border-[#d5a109] px-2 py-1 rounded-lg mr-1">
                        AMZ: {amazonCount}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Product Highlights Section */}
          {product.badges && product.badges.length > 0 && (
            <div className="border border-primary/20 rounded-xl overflow-hidden">
              <button
                onClick={() => toggleSection("badges")}
                className="w-full bg-secondary-bg hover:bg-primary/10 transition p-4 flex items-center justify-between"
              >
                <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                  Product Highlights
                </h3>
                <span className="text-primary/70">
                  {expandedSection.badges ? "−" : "+"}
                </span>
              </button>
              {expandedSection.badges && (
                <div className="p-4 border-t border-primary/20 bg-primary-bg">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="px-3 w-full py-1 rounded-lg border border-primary bg-green-900/30">
                      <span className="text-primary text-xs font-semibold">
                        {product.badges.length} Highlights Found
                      </span>
                    </div>
                  </div>
                  {product.badges.map((badge, idx) => (
                    <div
                      key={badge.id || idx}
                      className="flex justify-between items-center bg-secondary-bg p-4 rounded-lg mt-2"
                    >
                      <div style={{ textAlign: "left" }}>
                        <div className="text-white text-xs font-medium">
                          {badge.key ? badge.key.replace(/_/g, " ") : "Feature"}
                        </div>
                        <h4 className="text-sm font-semibold text-primary">
                          {badge.name || badge.id}
                        </h4>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Additional Images Gallery */}
          {images.length > 1 && (
            <div className="border border-primary/20 rounded-xl overflow-hidden">
              <button
                onClick={() => toggleSection("images")}
                className="w-full bg-secondary-bg hover:bg-primary/10 transition p-4 flex items-center justify-between"
              >
                <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                  Product Images
                </h3>
                <span className="text-primary/70">
                  {expandedSection.images ? "−" : "+"}
                </span>
              </button>
              {expandedSection.images && (
                <div className="p-4 border-t border-primary/20 bg-primary-bg">
                  <div className="flex gap-2 overflow-x-auto hide-scrollbar pb-2">
                    {images.map((img, idx) => (
                      <button
                        key={idx}
                        onClick={() => setSelectedImage(idx)}
                        className={`flex-shrink-0 w-16 h-16 bg-white rounded-lg overflow-hidden border-2 transition ${
                          selectedImage === idx
                            ? "border-primary"
                            : "border-transparent hover:border-primary/30"
                        }`}
                      >
                        <img
                          src={img}
                          alt={`Image ${idx + 1}`}
                          className="object-contain w-full h-full p-1"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Highlights Section */}
          {highlights.length > 0 && (
            <div className="border border-primary/20 rounded-xl overflow-hidden">
              <button
                onClick={() => toggleSection("highlights")}
                className="w-full bg-secondary-bg hover:bg-primary/10 transition p-4 flex items-center justify-between"
              >
                <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                  Product Highlights
                </h3>
                <span className="text-primary/70">
                  {expandedSection.highlights ? "−" : "+"}
                </span>
              </button>
              {expandedSection.highlights && (
                <div className="p-4 border-t border-primary/20 bg-primary-bg">
                  <ul className="space-y-2">
                    {highlights.map((highlight, idx) => (
                      <li
                        key={idx}
                        className="flex items-start gap-2 text-sm text-primary/90"
                      >
                        <span className="text-primary mt-1">•</span>
                        <span>{highlight}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Specifications Section */}
          {Object.keys(specs).length > 0 && (
            <div className="border border-primary/20 rounded-xl overflow-hidden">
              <button
                onClick={() => toggleSection("specifications")}
                className="w-full bg-secondary-bg hover:bg-primary/10 transition p-4 flex items-center justify-between"
              >
                <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                  Specifications
                </h3>
                <span className="text-primary/70">
                  {expandedSection.specifications ? "−" : "+"}
                </span>
              </button>
              {expandedSection.specifications && (
                <div className="p-4 border-t border-primary/20 bg-primary-bg">
                  <div className="grid grid-cols-1 gap-2">
                    {Object.entries(specs).map(([key, value], idx) => (
                      <div
                        key={idx}
                        className="flex justify-between items-start py-2 border-b border-primary/10 last:border-0"
                      >
                        <span className="text-xs text-primary/60 font-medium w-1/3">
                          {key.replace(/_/g, " ").toUpperCase()}
                        </span>
                        <span className="text-xs text-white font-medium w-2/3 text-right">
                          {value}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Variants Section */}
          {product.variant_swatches && product.variant_swatches.length > 0 && (
            <div className="border border-primary/20 rounded-xl overflow-hidden">
              <button
                onClick={() => toggleSection("variants")}
                className="w-full bg-secondary-bg hover:bg-primary/10 transition p-4 flex items-center justify-between"
              >
                <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                  Product Variants
                </h3>
                <span className="text-primary/70">
                  {expandedSection.variants ? "−" : "+"}
                </span>
              </button>
              {expandedSection.variants && (
                <div className="p-4 border-t border-primary/20 bg-primary-bg space-y-3">
                  {product.variant_swatches.map((variant, idx) => {
                    const selections = variant.available_selections || [];
                    const showAll = variantsExpanded[idx];
                    const visibleSelections = showAll
                      ? selections
                      : selections.slice(0, VARIANTS_VISIBLE_INITIAL);
                    const hasMore = selections.length > VARIANTS_VISIBLE_INITIAL;
                    const remainingCount =
                      selections.length - VARIANTS_VISIBLE_INITIAL;
                    return (
                      <div key={idx}>
                        <h4 className="text-xs font-semibold text-white mb-2 text-left">
                          {variant.name}
                        </h4>
                        <div className="flex gap-2 flex-wrap">
                          {visibleSelections.map((selection, sIdx) => (
                            <div
                              key={sIdx}
                              className="bg-secondary-bg border border-primary/30 rounded-lg p-3 hover:border-primary hover:bg-secondary transition w-full sm:w-auto sm:min-w-[120px]"
                            >
                              <div className="flex items-center gap-2">
                                {selection.swatch_image_url && (
                                  <div className="w-12 h-12 bg-white rounded overflow-hidden flex-shrink-0">
                                    <img
                                      src={selection.swatch_image_url}
                                      alt={selection.name}
                                      className="w-full h-full object-contain p-1"
                                    />
                                  </div>
                                )}
                                <div className="flex-1">
                                  <p className="text-xs font-medium text-white">
                                    {selection.name}
                                  </p>
                                  {selection.products &&
                                    selection.products[0] && (
                                      <div className="mt-1">
                                        <p className="text-sm font-bold text-primary">
                                          $
                                          {
                                            selection.products[0].price_map
                                              ?.price
                                          }
                                        </p>
                                        {selection.products[0].price_map
                                          ?.was_price && (
                                          <p className="text-[10px] text-primary/50 line-through">
                                            $
                                            {
                                              selection.products[0].price_map
                                                .was_price.price
                                            }
                                          </p>
                                        )}
                                        {selection.products[0].in_stock && (
                                          <p className="text-[10px] text-green-400">
                                            In Stock
                                          </p>
                                        )}
                                      </div>
                                    )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                        {hasMore && !showAll && (
                          <button
                            type="button"
                            onClick={() =>
                              setVariantsExpanded((prev) => ({
                                ...prev,
                                [idx]: true,
                              }))
                            }
                            className="mt-2 text-xs font-semibold text-primary hover:text-primary/80 underline"
                          >
                            Load more ({remainingCount} more)
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Categories Section */}
          {product.categories && product.categories.length > 0 && (
            <div className="border border-primary/20 rounded-xl overflow-hidden">
              <button
                onClick={() => toggleSection("categories")}
                className="w-full bg-secondary-bg hover:bg-primary/10 transition p-4 flex items-center justify-between"
              >
                <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                  Categories
                </h3>
                <span className="text-primary/70">
                  {expandedSection.categories ? "−" : "+"}
                </span>
              </button>
              {expandedSection.categories && (
                <div className="p-4 border-t border-primary/20 bg-primary-bg">
                  <div className="space-y-1">
                    {product.categories.map((cat, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-2 text-xs text-primary/90 py-1"
                      >
                        <span className="text-primary/50">›</span>
                        <span>{cat.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Reviews Summary */}
          <div className="border border-primary/20 rounded-xl overflow-hidden">
            <button
              onClick={() => toggleSection("reviews")}
              className="w-full bg-secondary-bg hover:bg-primary/10 transition p-4 flex items-center justify-between"
            >
              <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                Reviews Summary
              </h3>
              <span className="text-primary/70">
                {expandedSection.reviews ? "−" : "+"}
              </span>
            </button>
            {expandedSection.reviews && (
              <div className="p-4 border-t border-primary/20 bg-primary-bg">
                {reviewsLoading && (
                  <div className="text-xs text-primary/70">
                    Loading reviews...
                  </div>
                )}

                {!reviewsLoading && reviewsError && (
                  <div className="text-xs text-red-400">{reviewsError}</div>
                )}

                {!reviewsLoading &&
                  !reviewsError &&
                  (ratingBreakdown.length > 0 || ratingTotalCount > 0) && (
                    <>
                      <div className="flex items-center gap-4 mb-3">
                        <div className="text-3xl font-bold text-primary">
                          {typeof reviewsPayload.overall_rating === "number"
                            ? reviewsPayload.overall_rating.toFixed(1)
                            : product.rating?.value ?? "--"}
                        </div>
                        <div className="text-xs text-primary/70 space-y-0.5">
                          <div>
                            {ratingTotalCount.toLocaleString()}{" "}
                            {ratingTotalCount === 1 ? "review" : "reviews"}
                          </div>
                        </div>
                      </div>

                      {ratingBreakdown.length > 0 && (
                        <div className="space-y-1 mb-4">
                          {ratingBreakdown
                            .slice()
                            .sort((a, b) => b.stars - a.stars)
                            .map((row) => {
                              const total = ratingTotalCount || 0;
                              const pct =
                                total > 0
                                  ? Math.round(
                                      ((row.count || 0) / total) * 100
                                    )
                                  : 0;
                              return (
                                <div
                                  key={row.stars}
                                  className="flex items-center gap-2 text-[11px] text-primary/80"
                                >
                                  <span className="w-8 text-right">
                                    {row.stars}★
                                  </span>
                                  <div className="flex-1 h-2 bg-primary-bg rounded-full overflow-hidden">
                                    <div
                                      className="h-2 bg-primary rounded-full"
                                      style={{ width: `${pct}%` }}
                                    />
                                  </div>
                                  <span className="w-10 text-right">
                                    {pct}%
                                  </span>
                                </div>
                              );
                            })}
                        </div>
                      )}

                      {(topPositive || topNegative) && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                          {topPositive && (
                            <div className="bg-secondary-bg rounded-lg p-3 text-left flex flex-col gap-1">
                              <div className="flex items-center justify-between text-xs text-primary/70 mb-1">
                                <span className="font-semibold text-green-400">
                                  Top positive
                                </span>
                                {topPositive.rating != null && (
                                  <span className="text-[11px]">
                                    {topPositive.rating}★
                                  </span>
                                )}
                              </div>
                              {topPositive.title && (
                                <div className="text-sm font-semibold text-white">
                                  {topPositive.title}
                                </div>
                              )}
                              {topPositive.text && (
                                <div className="text-[11px] text-primary/80 line-clamp-4 whitespace-pre-line">
                                  {topPositive.text}
                                </div>
                              )}
                              <div className="flex items-center justify-between mt-2 text-[10px] text-primary/60">
                                <span>
                                  {topPositive.user_nickname ||
                                    "Verified purchaser"}
                                </span>
                                {topPositive.review_submission_time && (
                                  <span>
                                    {topPositive.review_submission_time}
                                  </span>
                                )}
                              </div>
                            </div>
                          )}

                          {topNegative && (
                            <div className="bg-secondary-bg rounded-lg p-3 text-left flex flex-col gap-1">
                              <div className="flex items-center justify-between text-xs text-primary/70 mb-1">
                                <span className="font-semibold text-red-400">
                                  Top negative
                                </span>
                                {topNegative.rating != null && (
                                  <span className="text-[11px]">
                                    {topNegative.rating}★
                                  </span>
                                )}
                              </div>
                              {topNegative.title && (
                                <div className="text-sm font-semibold text-white">
                                  {topNegative.title}
                                </div>
                              )}
                              {topNegative.text && (
                                <div className="text-[11px] text-primary/80 line-clamp-4 whitespace-pre-line">
                                  {topNegative.text}
                                </div>
                              )}
                              <div className="flex items-center justify-between mt-2 text-[10px] text-primary/60">
                                <span>
                                  {topNegative.user_nickname ||
                                    "Verified purchaser"}
                                </span>
                                {topNegative.review_submission_time && (
                                  <span>
                                    {topNegative.review_submission_time}
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WalmartProductPage;

